import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateString);
}

// Compact dd/mm/yy formatter for tabular date columns. Returns '—' for falsy
// inputs so callers don't need to guard.
export function formatShortDate(dateString) {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

export const priorityColors = {
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  urgent: 'bg-red-100 text-red-700 border-red-200',
};

// Solid swatch (left-bar / dot) per priority — used for color-coded indicators
export const priorityBar = {
  low: 'bg-emerald-500',
  medium: 'bg-amber-500',
  high: 'bg-orange-500',
  urgent: 'bg-red-600',
};

export const priorityLabel = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent / Critical',
};

export const statusColors = {
  open: 'bg-blue-100 text-blue-700 border-blue-200',
  new: 'bg-blue-100 text-blue-700 border-blue-200',
  assigned: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  in_progress: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  waiting: 'bg-amber-100 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-200 text-slate-700 border-slate-300',
};

export const statusLabels = {
  open: 'Open',
  new: 'New',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  pending: 'Waiting',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
};

// SLA helpers — relies on ticket.sla_due_at (ISO string)
export function getSLAStatus(ticket) {
  if (!ticket?.sla_due_at) return { state: 'none', label: 'No SLA' };
  const closed = ['resolved', 'closed'].includes(ticket.status);
  const due = new Date(ticket.sla_due_at);
  const now = new Date();
  const diffMs = due - now;
  if (closed) return { state: 'met', label: 'Met', diffMs };
  if (diffMs < 0) return { state: 'overdue', label: 'Overdue', diffMs };
  const hours = diffMs / 3600000;
  if (hours <= 2) return { state: 'risk', label: 'Due soon', diffMs };
  return { state: 'on_track', label: 'On track', diffMs };
}

export function isOverdue(ticket) {
  return getSLAStatus(ticket).state === 'overdue';
}

export function formatSLADue(ticket) {
  if (!ticket?.sla_due_at) return '—';
  const s = getSLAStatus(ticket);
  const due = new Date(ticket.sla_due_at);
  const absMs = Math.abs(s.diffMs || 0);
  const h = Math.floor(absMs / 3600000);
  const m = Math.floor((absMs % 3600000) / 60000);
  const rel = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : h >= 1 ? `${h}h ${m}m` : `${m}m`;
  if (s.state === 'overdue') return `${rel} overdue`;
  if (s.state === 'met') return `Met ${due.toLocaleDateString()}`;
  return `in ${rel}`;
}

export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

// Converts the backend's `_assignment_email` summary into a toast notification.
// Requires `toast` from 'sonner' to be passed in (so this file stays UI-free).
export function surfaceAssignmentEmailResultWith(toast, response) {
  const summary = response?._assignment_email;
  if (!summary) return; // Nothing to report (no assignee change)
  const { dispatched = [], skipped = [], reason } = summary;

  // Top-level no-op reasons (whole dispatch skipped)
  if (reason === 'notify_staff_off') {
    toast.warning('Assignment email not sent: "Notify staff on assignment" is OFF in Settings → Email.');
    return;
  }
  if (reason === 'zepto_not_configured') {
    toast.warning('Assignment email not sent: Zepto Mail is not configured / not enabled in Settings → Email.');
    return;
  }
  if (reason === 'helper_exception') {
    toast.error(`Assignment email helper crashed: ${summary.error || 'unknown error'}`);
    return;
  }

  const sent = dispatched.filter((d) => d.status === 'success');
  const failed = dispatched.filter((d) => d.status === 'error');
  const skippedRecipients = [
    ...skipped,
    ...dispatched.filter((d) => d.status === 'skipped'),
  ];

  if (sent.length) {
    toast.success(`Assignment email sent to ${sent.map((d) => d.name || d.email).join(', ')}.`);
  }
  skippedRecipients.forEach((s) => {
    const who = s.name || s.email || s.agent_id;
    const reasonMap = {
      agent_missing_email: `No email on file for ${who} — add one in User Management.`,
      agent_not_found: `Agent ${who} not found.`,
      not_configured: 'Zepto Mail not configured / not enabled.',
    };
    toast.warning(reasonMap[s.reason] || `Skipped ${who}: ${s.reason}`);
  });
  failed.forEach((f) => {
    toast.error(`Failed to email ${f.name || f.email}: ${typeof f.error === 'string' ? f.error : JSON.stringify(f.error)}`);
  });
}

export const TICKET_CATEGORIES = [
  'Hardware',
  'Software',
  'Network',
  'Account / Access',
  'Email',
  'Printer',
  'Server',
  'Other',
];
